import fetch, { storeService, twitchService } from "auth/FetchInterceptor";
import axios from "axios";
import { Moment } from "moment";
import {
  PlayerPersonalInfo,
  SearchCriterionRelatedAccounts,
} from "model/support";
import { RcFile } from "antd/lib/upload";
import CheckItemSlot from "views/app-views/support/components/check-item-slot";
import { remove } from "lodash";
import Search from "antd/lib/transfer/search";

const SupportService = {
  getPlayerData: function (
    playerId: string,
    gameId: string,
    isNick: boolean
  ): any {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/${isNick}`,
      method: "get",
    });
  },

  resolveNicknamesToAccountIds: function (
    nicknames: string[],
    gameId: string
  ): any {
    return fetch({
      url: `/support/${gameId}/players/resolve-nicknames`,
      method: "post",
      data: { nicknames },
    });
  },

  getGoldCount: function (gameId: string): any {
    return fetch({
      url: `/support/${gameId}/goldCount`,
      method: "get",
    });
  },

  getGuildDataById: function (guildId: string, gameId: string): any {
    return fetch({
      url: `/support/${gameId}/find-guild`,
      method: "get",
      params: { guildId },
    });
  },

  getGuildDataByName: function (guildName: string, gameId: string): any {
    return fetch({
      url: `/support/${gameId}/find-guild`,
      method: "get",
      params: { guildName },
    });
  },

  getGlobalBotData: function (gameId: string): any {
    return fetch({
      url: `/support/${gameId}/globalBotData`,
      method: "get",
    });
  },

  updateGlobalBotData: function (
    gameId: string,
    globalBotData: string,
    execute: boolean
  ): any {
    return fetch({
      url: `/support/${gameId}/updateGlobalBot`,
      method: "post",
      data: { data: globalBotData, Execute: execute },
    });
  },

  sendLogAnalyzer: function (
    gameId: string,
    startDate: Date,
    endDate: Date,
    query: string,
    outputType: string,
    params: string | null,
    folders: string[]
  ): any {
    return fetch({
      url: `/support/${gameId}/logAnalyzer`,
      method: "post",
      data: {
        Query: query,
        StartDate: startDate,
        EndDate: endDate,
        OutputType: outputType,
        Params: params,
        Folders: folders,
      },
    });
  },

  getLogAnalyzer: function (gameId: string): any {
    return fetch({
      url: `/support/${gameId}/getLogAnalyzer`,
      method: "get",
    });
  },

  updatePlayerData: function (
    gameId: string,
    playerId: string,
    personalInfo: PlayerPersonalInfo,
    amountCash: number,
    amountSeasonVault: number,
    amountSeasonIron: number,
    amountSeasonBronze: number,
    amountSeasonSilver: number,
    amountSeasonGold: number,
    amountSeasonPlatinum: number,
    amountSeasonDiamond: number
  ): any {
    return fetch({
      url: `/support/${gameId}/players/${playerId}`,
      method: "put",
      data: {
        personalInfo,
        amountCash,
        amountSeasonVault,
        amountSeasonIron,
        amountSeasonBronze,
        amountSeasonSilver,
        amountSeasonGold,
        amountSeasonPlatinum,
        amountSeasonDiamond,
      },
    });
  },

  unlinkPlayerFromServices: function (gameId: string, playerId: string): any {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/unlink`,
      method: "put",
    });
  },

  unlinkPlayerFromTwitch: function (playerId: string): any {
    return twitchService({
      url: `/unlink-twitch`,
      method: "delete",
      data: { gameAccount: playerId },
    });
  },

  getPlayerCurrencyTransactions: function (
    playerId: string,
    gameId: string,
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/transactions`,
      method: "get",
      params: { startRangeDate, endRangeDate },
    });
  },

  getPlayerPurchases: function (
    playerId: string,
    gameId: string,
    startRangeDate?: Date,
    endRangeDate?: Date
  ): any {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/itemPurchases`,
      method: "get",
      params: { startRangeDate, endRangeDate },
    });
  },

  getItemData: function (gameId: string, itemId: string): any {
    return fetch({
      url: `/support/${gameId}/items/${itemId}`,
      method: "get",
    });
  },

  sendItem: function (
    gameId: string,
    playerIds: string[],
    itemId: string,
    amount: number,
    csv: string,
    description: string
  ): any {
    return fetch({
      url: `/support/${gameId}/sendItem`,
      method: "post",
      data: { itemId, amount, playerIds, csv, description },
    });
  },

  getPlayerImportItemHistory: function (gameId: string, playerId: string): any {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/importItemHistory`,
      method: "get",
    });
  },

  restartGuildMarkUpdateTimer: function (
    gameId: string,
    guildId?: string
  ): any {
    return fetch({
      url: `/support/${gameId}/guilds/restartGuildMarkUpdateTimer`,
      method: "put",
      data: {
        guildId,
      },
    });
  },

getIpAddress: async function (): Promise<any> {
    const isIpv4Format = (ip: string) => {
      const regex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/;
      return regex.test(ip);
    };

    const sources = [
      async () => {
        const response = await axios.get("https://api.ipify.org?format=json");
        return response.data.ip;
      },
      async () => {
        const response = await axios.get("https://ipv4.jsonip.com");
        return response.data.ip;
      },
      async () => {
        const response = await axios.get("https://ipv4.icanhazip.com/");
        return response.data.trim();
      },
      async () => {
        const response = await axios.get("https://checkip.amazonaws.com/");
        return response.data.trim();
      },
    ];

    for (const source of sources) {
      try {
        const ip = await source();
        if (ip && isIpv4Format(ip)) {
          return { ip };
        }
      } catch (error) {
        // continue to next source
      }
    }

    throw new Error("Unable to retrieve a valid IPv4 address from any source.");
  },

  requestAccessToGmTools: function (
    ipAddress: string,
    gameId: string,
    userEmail: string
  ): any {
    return fetch({
      url: `/support/${gameId}/gms`,
      method: "put",
      data: {
        ipAddress,
        userEmail,
      },
    });
  },

  getPlayerBanHistory: function (
    gameId: string,
    playerId: string
  ): Promise<any> {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/banHistory`,
      method: "get",
    });
  },

  uploadBanImages: function (
    gameId: string,
    imageFiles: RcFile[]
  ): Promise<any> {
    const formData = new FormData();
    formData.append("gameId", gameId);

    imageFiles.forEach((file) => {
      formData.append("imageFiles", file); // <-- aqui sim vai o File binário
    });

    return fetch({
      url: `/support/uploadBanReasonImages`,
      method: "post",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  banPlayer: function (
    gameId: string,
    playerId: string,
    reason: string,
    comment: string | undefined,
    reasonImages: string[] | undefined,
    bannedBy: string,
    severity: string,
    permanent: boolean,
    temporary: boolean,
    until: Moment | undefined
  ): Promise<any> {
    console.log(gameId);
    return fetch({
      url: `/support/${gameId}/players/${playerId}/banAccount`,
      method: "post",
      data: {
        reason,
        comment,
        reasonImages,
        bannedBy,
        severity,
        permanent,
        temporary,
        until,
      },
    });
  },

  banPlayers: function (
    gameId: string,
    accounts: string[],
    reason: string,
    comment: string | undefined,
    reasonImages: string[] | undefined,
    bannedBy: string,
    severity: string,
    permanent: boolean,
    temporary: boolean,
    until: Moment | undefined
  ): Promise<any> {
    return fetch({
      url: `/support/${gameId}/players/banAccounts`,
      method: "post",
      data: {
        reason,
        accounts,
        comment,
        reasonImages,
        bannedBy,
        severity,
        permanent,
        temporary,
        until,
      },
    });
  },

  unbanPlayer: function (
    gameId: string,
    playerId: string,
    unbannedBy: string,
    unbanComment: string | undefined
  ): Promise<any> {
    console.log(gameId);
    return fetch({
      url: `/support/${gameId}/players/${playerId}/unbanAccount`,
      method: "post",
      data: {
        unbannedBy,
        unbanComment,
      },
    });
  },

  getIsPlayerBanned: function (gameId: string, playerId: string): Promise<any> {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/isBanned`,
      method: "get",
    });
  },

  getPaidTrafficEmailsData: function (): Promise<any> {
    return fetch({
      url: `/support/paidTrafficEmails/`,
      method: "get",
    });
  },

  getAllEmailsData: function (): Promise<any> {
    return fetch({
      url: `/support/allEmails/`,
      method: "get",
    });
  },

  getServicesStatus: function (gameId: string): Promise<any> {
    return fetch({
      url: `/operation/${gameId}/services`,
      method: "get",
    });
  },

  updateServiceStatus: function (
    gameId: string,
    service: string,
    status: boolean
  ): Promise<any> {
    return fetch({
      url: `/operation/${gameId}/services`,
      method: "post",
      data: {
        service,
        status,
      },
    });
  },

  blockTMS: function (
    gameId: string,
    tm_id: string,
    type: string
  ): Promise<any> {
    return fetch({
      url: `/operation/${gameId}/blockTMS`,
      method: "post",
      data: {
        tm_id,
        type,
      },
    });
  },

  getURLShorterMetrics: function (): Promise<any> {
    return fetch({
      url: `/support/urlMetrics`,
      method: "get",
    });
  },

  getAccountsNP: function (): Promise<any> {
    return fetch({
      url: `/support/accountsNP`,
      method: "get",
    });
  },

  getGmAccounts: function (): Promise<any> {
    return fetch({
      url: `/support/gm-accounts`,
      method: "get",
    });
  },

  deleteGmAccount: function (id: string): Promise<any> {
    return fetch({
      url: `/support/gm-accounts/`,
      method: "delete",
      data: { username: id },
    });
  },

  updateGmAccount: function (id: string, mac: string): Promise<any> {
    return fetch({
      url: `/support/gm-accounts/`,
      method: "patch",
      data: { username: id, mac },
    });
  },

  addGmAccount: function (id: string): Promise<any> {
    return fetch({
      url: `/support/gm-accounts/`,
      method: "post",
      data: { username: id },
    });
  },

  logAction: function (username: string, action: string): Promise<any> {
    return fetch({
      url: `/support/log-action`,
      method: "post",
      data: { username, action, source: "DASHBOARD" },
    });
  },

  getActionLogs: function (): Promise<any> {
    return fetch({
      url: `/support/log-action`,
      method: "get",
    });
  },

  getPRRanking: function (): Promise<any> {
    return fetch({
      url: `/support/pr-ranking`,
      method: "get",
    });
  },

  getTwitchUsers: function (): Promise<any> {
    return fetch({
      url: `/support/twitch-users`,
      method: "get",
    });
  },

  getTwitchDropStatus: function (playerId: string): Promise<any> {
    return fetch({
      url: `/support/players/${playerId}/twitch-drop-status`,
      method: "get",
    });
  },

  getOnlineInventory: function (playerId: string, gameId: string): Promise<any> {
    return fetch({
      url: `/support/${gameId}/players/${playerId}/online-inventory`,
      method: "get",
    });
  },

  getTwitchAnalytics: function (startDate?: string, endDate?: string): Promise<any> {
    return fetch({
      url: `/support/twitch-analytics`,
      method: "get",
      params: { startDate, endDate },
    });
  },

  getTwitchDrops: function (): Promise<any> {
    return fetch({
      url: `/support/twitch-drops`,
      method: "get",
    });
  },

  createTwitchDrop: function (benefitId: string): Promise<any> {
    return fetch({
      url: `/support/twitch-drops`,
      method: "post",
      data: { benefit_id: benefitId },
    });
  },

  getTwitchAbuseSuspects: function (
    startDate?: string,
    endDate?: string,
    minDistinctTwitchNames?: number,
    minAccountsPerTwitch?: number
  ): Promise<any> {
    return fetch({
      url: `/support/twitch-abuse-suspects`,
      method: "get",
      params: { startDate, endDate, minDistinctTwitchNames, minAccountsPerTwitch },
    });
  },

  getItemTrackingHistory: function (
    username: string,
    itemId: number,
    startDate: Date
  ): Promise<any> {
    return fetch({
      url: `/support/item-tracking`,
      method: "get",
      params: { username, itemId, startDate },
    });
  },

  getAccounstByDate: function ({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate?: Date;
  }): Promise<any> {
    return fetch({
      url: `/support/get-accounts-from-date-and-balance`,
      method: "post",
      data: {
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate?.toISOString().split("T")[0],
      },
      responseType: "blob",
    });
  },

  getEmailsByDateRange: function (
    gameId: string,
    startDate: Date,
    endDate: Date,
    minSpend: number,
    maxSpend: number
  ): Promise<any> {
    return fetch({
      url: `/support/${gameId}/emails-by-date-range`,
      method: "post",
      data: {
        GameId: gameId,
        LoginStart: startDate,
        LoginEnd: endDate,
        MinSpend: minSpend,
        MaxSpend: maxSpend,
      },
    });
  },

  getAccountsByLastLogin: function ({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }): Promise<any> {
    return fetch({
      url: `/support/accounts-by-last-login`,
      method: "post",
      data: {
        StartDate: startDate.toISOString().split("T")[0],
        EndDate: endDate.toISOString().split("T")[0],
      },
      responseType: "blob",
    });
  },

  addObservation: function (
    playerId: string,
    author: string,
    observation: string
  ): any {
    return fetch({
      url: `/support/players/${playerId}/observation`,
      method: "post",
      data: { author, observation },
    });
  },

  getRefundsByType: function (type: string): Promise<any> {
    return fetch({
      url: `/support/get-refunds-by-type/${type}`,
      method: "get",
    });
  },
  getRefundsDetailByType: function (type: string): Promise<any> {
    return fetch({
      url: `/support/get-refunds-detail-by-type/${type}`,
      method: "get",
    });
  },

  getPurchasesByConclusionMonth: function (
    type: string,
    status: string
  ): Promise<any> {
    return fetch({
      url: `/support/get-purchases-by-conclusion-month/${type}/${status}`,
      method: "get",
    });
  },

  setPublisherReportForMonth: function (data: {
    month: string;
    type: string;
    status: string;
    reported: boolean;
    email: string;
  }): Promise<any> {
    return fetch({
      url: `/support/set-publisher-report-for-month`,
      method: "put",
      data,
    });
  },

  updateGenericRefund: function (data: any): Promise<any> {
    return fetch({
      url: `/support/update-generic-refund/`,
      method: "put",
      data,
    });
  },

  getRefundLogs: function (tid: string): Promise<any> {
    return fetch({
      url: `/support/get-refund-logs/`,
      method: "get",
      params: { tid },
    });
  },

  removeItemsNpFromSourceAccount: function (data: any): Promise<any> {
    return fetch({
      url: `/support/remove-items-np-from-source-account`,
      method: "post",
      data,
    });
  },

  cancelPurchase: function (data: any): Promise<any> {
    return fetch({
      url: `/support/cancel-purchase`,
      method: "post",
      data,
    });
  },

  getRelatedAccounts: function (
    searchList: {
      type: SearchCriterionRelatedAccounts["type"];
      value: string;
    }[],
    server: string
  ): Promise<any> {
    return fetch({
      url: `/support/get-related-accounts`,
      method: "post",
      data: { searchList, server },
    });
  },

  sendCustomEmail: function (
    html: string,
    title: string,
    email: string,
    code: string,
    sender: string,
    accountname: string
  ): Promise<any> {
    return fetch({
      url: `/support/send-custom-email`,
      method: "post",
      data: { html, title, email, code, accountname, sender },
    });
  },

  getCustomEmailCodes: function (accountname: string): Promise<any> {
    return fetch({
      url: `/support/get-custom-email-codes`,
      method: "get",
      params: { accountname },
    });
  },

  updateCustomEmailCode: function (
    accountname: string,
    code: string,
    status: number,
    observation: string,
    author: string
  ): Promise<any> {
    return fetch({
      url: `/support/update-custom-email-code/`,
      method: "put",
      data: { accountname, code, status, observation, author },
    });
  },

  getCustomEmailLogs: function (
    accountname: string,
    code: string
  ): Promise<any> {
    return fetch({
      url: `/support/get-custom-email-logs/`,
      method: "get",
      params: { accountname, code },
    });
  },

  getBackupVersionsId: function ({
    accountName,
    keyMarker,
    versionIdMarker,
    server = "gcore",
  }: {
    accountName: string;
    keyMarker?: string;
    versionIdMarker?: string;
    server?: "gcore" | "ovh";
  }): Promise<any> {
    return fetch({
      url: `/support/backup-id-history-account`,
      method: "get",
      params: { accountName, keyMarker, versionIdMarker, server },
    });
  },

  getBackupFile: function ({
    accountName,
    versionId,
    server = "gcore",
  }: {
    accountName: string;
    versionId: string;
    server?: "gcore" | "ovh";
  }): Promise<any> {
    return fetch({
      url: `/support/backup-account`,
      method: "get",
      params: { accountName, versionId, server },
    });
  },

  gePunishments: function (): Promise<any> {
    return fetch({
      url: `/support/get-punishment-list`,
      method: "get",
    });
  },

  addIncident: function ({
    title,
    title_en,
    status,
    description,
    description_en,
    author
  }: {
    title: string;
    title_en: string;
    status: string;
    description: string;
    description_en: string;
    author: string;
  }): Promise<any> {
    return fetch({
      url: `/support/create-incident/`,
      method: "post",
      data: {title,title_en, status, description, description_en, author},
    });
  },

  addIncidentUpdate: function ({
    incident_Id,
    description,
    description_en,
    author,
    status,
  }: {
    incident_Id: number;
    description: string;
    description_en: string;
    author: string;
    status: string;
  }): Promise<any> {
    return fetch({
      url: `/support/create-incident-update/`,
      method: "post",
      data: { incident_Id, description, description_en, author, status },
    });
  },

  getIncidents: function (): Promise<any> {
    return fetch({
      url: `/support/get-incidents/`,
      method: "get",
    });
  },

  getIncidentUpdates: function (incident_Id: number): Promise<any> {
    return fetch({
      url: `/support/get-incident-updates/`,
      method: "get",
      params: { incident_Id },
    });
  },

  deleteIncident: function (IncidentId: number): Promise<any> {
    return fetch({
      url: `/support/delete-incident/`,
      method: "delete",
      data: { IncidentId },
    });
  },

  deleteIncidentUpdate: function (Update_Id: number): Promise<any> {
    return fetch({
      url: `/support/delete-incident-update/`,
      method: "delete",
      data: { Update_Id },
    });
  },

  updateIncidentInfo: function (
    data: { incidentID: number; title?: string; title_en?: string; description?: string; description_en?: string; status?: string }
  ): Promise<any> {
    return fetch({
      url: `/support/modify-incident-info/`,
      method: "put",
      data,
    });
  },

  modifyIncidentUpdate: function (
    data: { update_ID: number; incidenT_ID: number; description?: string; description_en?: string; status?: string }
  ): Promise<any> {
    return fetch({
      url: `/support/modify-incident-update/`,
      method: "put",
      data,
    });
  },

  checkItemSlot: function (
    gameId: string,
    accountName: string,
    itemId: number
  ): any {
    return fetch({
      url: `/support/${gameId}/check-item-slot`,
      method: "get",
      params: { accountName, itemId },
    });
  },

  getKeyItems: function (): any {
    return fetch({
      url: `/support/get-key-items`,
      method: "get",
    });
  },

  addKeyItems: function (itemIds: string[]): any {
    return fetch({
      url: `/support/add-key-items`,
      method: "post",
      data: { itemIds },
    });
  },

  removeKeyItem: function (itemId: string[]): any {
    return fetch({
      url: `/support/remove-key-item`,
      method: "delete",
      data: { itemId },
    });
  },

  SearchKeyItems: function (
    gameId: string,
    accountname: string,
    itemIds: string
  ): any {
    return fetch({
      url: `/support/${gameId}/check-key-items-slot`,
      method: "get",
      params: { accountname, itemIds },
    });
  },

  CostumeFinder: function (costumeId: string): any {
    return fetch({
      url: `/support/costume-finder`,
      method: "get",
      params: { costumeId },
    });
  },

  RoyalArenaRank: function (month: number): any {
    return fetch({
      url: `/support/royal-rank-dashboard`,
      method: "get",
      params: { month },
    });
  },

  AddRefunds: function (data: any): any {
    return fetch({
      url: `/support/refund/insert`,
      method: "post",
      data,
    });
  },

  getAccountsByTid: function (tid: string): any {
    return fetch({
      url: `/support/get-accounts-by-tid/${tid}`,
      method: "get",
    });
  },

  GetRefundFilesFromS3: function (refundTid: string): any {
    return fetch({
      url: `/support/get-refund-files-from-s3/${refundTid}`,
      method: "get"
    });
  },

  UploadRefundFileToS3: function (requestData: any): any {
    return fetch({
      url: `/support/upload-refund-file-to-s3`,
      method: "post",
      data: requestData,
    });
  },

  DeleteRefundFileFromS3: function (requestData: any): any {
    return fetch({
      url: `/support/delete-refund-file-from-s3`,
      method: "delete",
      data: requestData,
    });
  },

  downloadRefundFileFromS3: function (filepath: string): any {
    return fetch({
      url: `/support/download-refund-file-from-s3`,
      method: "post",
      data: {
        FullPath: filepath
      }
    });
  },

  getPossiblePackagesForTransfer: function (tid: string): Promise<any> {
    return fetch({
      url: `/support/get-possible-packages-for-transfer/${tid}`,
      method: "get",
    });
  },

  transferPurchase: function (data: any): Promise<any> {
    return fetch({
      url: `/support/transfer-purchase-deliver`,
      method: "post",
      data,
    });
  },

  getJackalEvolutionData: function (
    GameId:string
  ): any {
    return fetch({
      url: `/support/jackal-evolution`,
      method: "get",
      params: { GameId },
    });
  },

  searchAccountMuteInfo: function (accountName: string, server: string): any {
    return fetch({
      url: `/support/search-account-mute-info`,
      method: "post",
      data: { accountName, server },
    });
  },

  muteAccount: function (data: FormData): any {
    return fetch({
      url: `/support/mute-account`,
      method: "post",
      data,
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  unmuteAccount: function (data: FormData): any {
    return fetch({
      url: `/support/unmute-account`,
      method: "post",
      data,
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  getAccountStatus: function (accountName: string, server: string): any {
    return fetch({
      url: `/support/account-status`,
      method: "post",
      data: { accountName, server },
    });
  },

  getAccountItems: function (accountName: string, server: string): any {
    return fetch({
      url: `/support/get-account-items`,
      method: "post",
      data: { accountName, server },
    });
  },

  editItem: function (data: {
    accountName: string;
    server: string;
    container: string; // equip | inventory | cargo
    charIndex: number;
    slot: number;
    itemLine: string; // "id o v o v o v amount" ou "clear"
    operatorEmail?: string;
  }): any {
    return fetch({
      url: `/support/edit-item`,
      method: "post",
      data,
    });
  },

  editItems: function (data: {
    accountName: string;
    server: string;
    operations: { container: string; charIndex: number; slot: number; itemLine: string }[];
    operatorEmail?: string;
  }): any {
    return fetch({
      url: `/support/edit-items`,
      method: "post",
      data,
    });
  },

  getMuteUnmuteLogs: function (accountName: string, server: string): any {
    return fetch({
      url: `/support/get-mute-unmute-logs`,
      method: "get",
      params: { accountName, server }
    });
  },

  paymentRefund: function (data: { login: string; amountCent: number; currencyCode: string }): any {
    return storeService({
      url: `/payment/refund`,
      method: "post",
      data,
    });
  },
};

export default SupportService;
